// 🎮 Playground 탭 - Storage/Connectivity 정규화 실험
// [배경] 입력값을 즉시 정규화하고 예상 priceId를 보여주는 실험 도구

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
  PlayArrow as TestIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon
} from '@mui/icons-material';

import { SSOTTestService } from '../../../../services/ssotTestService';
import { DeviceType } from '../../../../types/deviceCatalog';

// 사전 정의된 테스트 케이스
const PRESET_TEST_CASES = [
  { name: '아이폰 128GB', storage: '128GB', connectivity: 'Wi-Fi', deviceType: 'smartphone' as DeviceType },
  { name: '아이패드 프로 256GB Wi-Fi', storage: '256GB', connectivity: 'Wi-Fi', deviceType: 'tablet' as DeviceType },
  { name: '아이패드 프로 256GB 셀룰러', storage: '256GB', connectivity: 'Wi-Fi + Cellular', deviceType: 'tablet' as DeviceType },
  { name: '에어팟 프로', storage: '기본', connectivity: '', deviceType: 'wearable' as DeviceType },
  { name: '512 GB (공백 포함)', storage: '512 GB', connectivity: 'wifi', deviceType: 'smartphone' as DeviceType },
  { name: '1TB 대용량', storage: '1TB', connectivity: 'Cellular', deviceType: 'tablet' as DeviceType },
  { name: '64gb 소문자', storage: '64gb', connectivity: 'WIFI', deviceType: 'smartphone' as DeviceType },
  { name: 'LTE 연결', storage: '256GB', connectivity: 'LTE', deviceType: 'tablet' as DeviceType },
];

/**
 * 🎮 Playground 탭 컴포넌트
 * - storage/connectivity 입력값 정규화 실험
 * - 예상 priceId 미리보기
 */
const PlaygroundTab: React.FC = () => {
  // === 상태 ===
  const [inputStorage, setInputStorage] = useState('256GB');
  const [inputConnectivity, setInputConnectivity] = useState('Wi-Fi');
  const [deviceType, setDeviceType] = useState<DeviceType>('smartphone');
  const [modelId, setModelId] = useState('apple-smartphone-iphone-16-iphone-16-pro');
  
  // 배치 테스트 결과
  const [batchResults, setBatchResults] = useState<Array<{
    name: string;
    input: { storage: string; connectivity: string };
    normalized: { storage: string | null; connectivity: string | null };
    priceId: string;
    passed: boolean;
  }>>([]);
  
  // === 정규화 결과 계산 ===
  const normalizeResult = useMemo(() => {
    return SSOTTestService.normalizeExperiment(
      inputStorage,
      inputConnectivity,
      deviceType,
      modelId
    );
  }, [inputStorage, inputConnectivity, deviceType, modelId]);
  
  // === 클립보드 복사 ===
  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);
  
  // === 프리셋 적용 ===
  const handleApplyPreset = useCallback((preset: typeof PRESET_TEST_CASES[0]) => {
    setInputStorage(preset.storage);
    setInputConnectivity(preset.connectivity);
    setDeviceType(preset.deviceType);
  }, []);
  
  // === 배치 테스트 실행 ===
  const handleRunBatchTest = useCallback(() => {
    const results = PRESET_TEST_CASES.map(preset => {
      const result = SSOTTestService.normalizeExperiment(
        preset.storage,
        preset.connectivity,
        preset.deviceType,
        modelId
      );
      
      // 예상 결과 검증 (정규화가 제대로 되었는지)
      const storageOk = preset.deviceType === 'wearable' 
        ? result.normalizedStorage === null
        : result.normalizedStorage !== null;
      
      const connectivityOk = 
        (preset.connectivity === '' || preset.connectivity === 'Wi-Fi' || preset.connectivity.toLowerCase() === 'wifi')
          ? result.normalizedConnectivity === null
          : result.normalizedConnectivity === 'cellular';
      
      return {
        name: preset.name,
        input: { storage: preset.storage, connectivity: preset.connectivity },
        normalized: { storage: result.normalizedStorage, connectivity: result.normalizedConnectivity },
        priceId: result.expectedPriceId,
        passed: storageOk && connectivityOk
      };
    });
    
    setBatchResults(results);
  }, [modelId]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          저장소와 연결 방식 입력값이 어떻게 정규화되는지 실험하고, 예상 가격 ID를 확인할 수 있습니다.
        </Typography>
      </Alert>
      
      {/* 입력 영역 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          입력값
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Model ID"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              helperText="priceId 생성에 사용될 modelId"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>디바이스 타입</InputLabel>
              <Select
                value={deviceType}
                label="디바이스 타입"
                onChange={(e) => setDeviceType(e.target.value as DeviceType)}
              >
                <MenuItem value="smartphone">스마트폰</MenuItem>
                <MenuItem value="tablet">태블릿</MenuItem>
                <MenuItem value="wearable">웨어러블</MenuItem>
              </Select>
            </FormControl>
          </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="저장소"
                value={inputStorage}
                onChange={(e) => setInputStorage(e.target.value)}
                placeholder="256GB, 512 GB, 1TB..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="연결 방식"
                value={inputConnectivity}
                onChange={(e) => setInputConnectivity(e.target.value)}
                placeholder="Wi-Fi, Wi-Fi + Cellular, Cellular..."
              />
            </Grid>
        </Grid>
        
        {/* 프리셋 버튼 */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            프리셋 테스트 케이스:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PRESET_TEST_CASES.slice(0, 5).map((preset, idx) => (
              <Chip
                key={idx}
                label={preset.name}
                size="small"
                variant="outlined"
                onClick={() => handleApplyPreset(preset)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      </Paper>
      
        {/* 정규화 결과 */}
        <Grid container spacing={3}>
          {/* 저장소 정규화 */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  저장소 정규화
                </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">입력</Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    "{inputStorage}"
                  </Typography>
                </Box>
                <Typography variant="h5" color="text.secondary">→</Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">정규화</Typography>
                  <Typography 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontWeight: 700,
                      color: normalizeResult.normalizedStorage ? 'success.main' : 'warning.main'
                    }}
                  >
                    {normalizeResult.normalizedStorage === null 
                      ? 'null (가격 ID에 미포함)'
                      : `"${normalizeResult.normalizedStorage}"`}
                  </Typography>
                </Box>
              </Box>
              
              <Alert 
                severity={deviceType === 'wearable' ? 'info' : 'success'} 
                sx={{ mt: 2 }}
                icon={<CodeIcon />}
              >
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  normalizeStorage("{inputStorage}", "{deviceType}")
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
        
          {/* 연결 방식 정규화 */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  연결 방식 정규화
                </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">입력</Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    "{inputConnectivity}"
                  </Typography>
                </Box>
                <Typography variant="h5" color="text.secondary">→</Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">정규화</Typography>
                  <Typography 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontWeight: 700,
                      color: normalizeResult.normalizedConnectivity ? 'info.main' : 'warning.main'
                    }}
                  >
                    {normalizeResult.normalizedConnectivity === null 
                      ? 'null (가격 ID에 미포함)'
                      : `"${normalizeResult.normalizedConnectivity}"`}
                  </Typography>
                </Box>
              </Box>
              
              <Alert 
                severity="info" 
                sx={{ mt: 2 }}
                icon={<CodeIcon />}
              >
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  normalizeConnectivity("{inputConnectivity}")
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* 예상 가격 ID */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 3, 
          mt: 3, 
          bgcolor: 'primary.50',
          border: '2px solid',
          borderColor: 'primary.main'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              예상 가격 ID
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}
            >
              {normalizeResult.expectedPriceId}
            </Typography>
          </Box>
          <Tooltip title="복사">
            <IconButton onClick={() => handleCopy(normalizeResult.expectedPriceId)}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* 가격 ID 구성 설명 */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          가격 ID 구성:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`모델 ID: ${modelId}`} size="small" />
          {normalizeResult.normalizedStorage && (
            <Chip label={`저장소: ${normalizeResult.normalizedStorage}`} size="small" color="success" />
          )}
          {normalizeResult.normalizedConnectivity && (
            <Chip label={`연결: ${normalizeResult.normalizedConnectivity}`} size="small" color="info" />
          )}
          <Chip label="지역: kr" size="small" variant="outlined" />
        </Box>
      </Paper>
      
      <Divider sx={{ my: 4 }} />
      
      {/* 일괄 테스트 */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>
          일괄 테스트
        </Typography>
        <Button
          variant="contained"
          startIcon={<TestIcon />}
          onClick={handleRunBatchTest}
        >
          모든 프리셋 테스트
        </Button>
      </Box>
      
      {batchResults.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>테스트 케이스</TableCell>
                    <TableCell>입력</TableCell>
                    <TableCell>정규화 결과</TableCell>
                    <TableCell>예상 가격 ID 접미사</TableCell>
                    <TableCell align="center">결과</TableCell>
                  </TableRow>
                </TableHead>
            <TableBody>
              {batchResults.map((result, idx) => (
                <TableRow 
                  key={idx}
                  sx={{ bgcolor: result.passed ? 'success.50' : 'error.50' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {result.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      S: {result.input.storage || '(없음)'}
                      <br />
                      C: {result.input.connectivity || '(없음)'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      S: {result.normalized.storage ?? 'null'}
                      <br />
                      C: {result.normalized.connectivity ?? 'null'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {result.priceId.split(modelId + '-')[1] || result.priceId}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {result.passed ? (
                      <PassIcon color="success" />
                    ) : (
                      <FailIcon color="error" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* SSOT 규칙 설명 */}
      <Paper variant="outlined" sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          📖 SSOT 정규화 규칙
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              저장소 정규화
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • 웨어러블: 항상 null (가격 ID에 미포함)
              <br />
              • '기본', 'default', 'base': null
              <br />
              • 그 외: 소문자 + 숫자만 추출 (예: "256 GB" → "256gb")
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="info.main" gutterBottom>
              연결 방식 정규화
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • 'Wi-Fi', '기본', '', undefined: null (가격 ID에 미포함)
              <br />
              • 'Wi-Fi + Cellular', 'Cellular', 'LTE', '5G': 'cellular'
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PlaygroundTab;

